import { useMemo, useState } from 'react';

import { RowSelectionState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SubsetSuggestionStatus } from '../../../../../../../../backend/src/case/subset/subset-suggestion.entity';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateDocSubset } from '@/api/mutations/useCreateDocSubset';
import { useUpdateSubset } from '@/api/mutations/useUpdateSubset';
import { useUpdateSuggestionStatus } from '@/api/mutations/useUpdateSuggestionStatus';
import { useGetMatterDocs } from '@/api/queries/useGetMatterDocs';
import { useGetProcessingDocs } from '@/api/queries/useGetProcessingDocs';
import { useGetSubsets } from '@/api/queries/useGetSubsets';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Popover, PopoverTrigger, PopoverContent } from '@/new/components/shadcn/popover';
import { Textarea } from '@/new/components/shadcn/textarea';
import { DocPickerTable } from '@/new/pages/matters/:matterId/subsets/new/components/doc-picker-table';

interface SubsetFormProps {
  mode: 'create' | 'edit';
  matterId: string;
  subsetId?: string;
  initialData?: {
    name: string;
    legalIssues: string;
    selectedDocIds: string[];
  };
  suggestionId?: string;
  isLoadingInitialData?: boolean;
  onSuccess?: (subsetId: string) => void;
  matterIssues?: string;
}

export function SubsetForm({
  mode,
  matterId,
  subsetId,
  initialData,
  suggestionId,
  isLoadingInitialData,
  onSuccess,
  matterIssues,
}: SubsetFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: docs, isLoading: isLoadingDocs } = useGetMatterDocs(matterId);
  const { data: subsets } = useGetSubsets(matterId);
  const { data: processingDocsData } = useGetProcessingDocs(matterId);
  const createSubset = useCreateDocSubset();
  const updateSubset = useUpdateSubset();
  const updateSuggestionStatus = useUpdateSuggestionStatus();

  // Create a Set of processing document IDs for efficient lookup
  const processingDocIds = useMemo(() => new Set(processingDocsData?.docIds ?? []), [processingDocsData?.docIds]);

  // Find the all docs case ID for NL filtering since for now we need that to build NL filters
  const allDocumentsCase = useMemo(() => {
    return subsets?.find((subset) => subset.isAllDocuments);
  }, [subsets]);

  // Initialize form state with initial data if in edit mode
  const [subsetName, setSubsetName] = useState(initialData?.name ?? '');
  const [subsetIssues, setSubsetIssues] = useState(initialData?.legalIssues ?? '');

  // Form validation state
  const [showNameError, setShowNameError] = useState(false);
  const [showIssuesError, setShowIssuesError] = useState(false);

  // Confirmation dialog state for edit mode
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Check if values have changed from initial
  const nameHasChanged = useMemo(() => subsetName !== (initialData?.name ?? ''), [subsetName, initialData?.name]);
  const issuesHaveChanged = useMemo(
    () => subsetIssues !== (initialData?.legalIssues ?? ''),
    [subsetIssues, initialData?.legalIssues],
  );

  // Initialize row selection from initial data
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
    if (initialData?.selectedDocIds) {
      return initialData.selectedDocIds.reduce((acc, docId) => {
        acc[docId] = true;
        return acc;
      }, {} as RowSelectionState);
    }
    return {};
  });

  // Track initial document IDs for edit mode comparison
  const initialDocIds = useMemo(() => initialData?.selectedDocIds ?? [], [initialData]);

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);
  const selectedDocIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  // Calculate document changes for edit mode
  const documentChanges = useMemo(() => {
    if (mode !== 'edit') return { added: 0, removed: 0 };

    const addedDocs = selectedDocIds.filter((id) => !initialDocIds.includes(id));
    const removedDocs = initialDocIds.filter((id) => !selectedDocIds.includes(id));

    return {
      added: addedDocs.length,
      removed: removedDocs.length,
    };
  }, [mode, selectedDocIds, initialDocIds]);

  // Check if any changes have been made in edit mode
  const hasChanges = useMemo(() => {
    if (mode !== 'edit' || !initialData) return true;

    const nameChanged = subsetName !== initialData.name;
    const issuesChanged = subsetIssues !== initialData.legalIssues;
    const docsChanged = documentChanges.added > 0 || documentChanges.removed > 0;

    return nameChanged || issuesChanged || docsChanged;
  }, [mode, initialData, subsetName, subsetIssues, documentChanges]);

  const handleButtonClick = () => {
    // Validate form fields
    const hasNameError = !subsetName.trim();
    const hasIssuesError = !subsetIssues.trim();

    setShowNameError(hasNameError);
    setShowIssuesError(hasIssuesError);

    // Don't proceed if validation fails or no docs selected
    if (hasNameError || hasIssuesError || selectedDocIds.length === 0) return;

    // Show confirmation dialog for edit mode, otherwise submit directly
    if (mode === 'edit') {
      setShowConfirmDialog(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    try {
      let result: { id: string } | undefined = undefined;

      if (mode === 'create') {
        result = await createSubset.mutateAsync({
          matterId: matterId,
          body: {
            title: subsetName,
            legalIssues: subsetIssues,
            docIds: selectedDocIds,
          },
        });

        // Track subset creation
        if (result) {
          trackCustomEvent(TrackingEventsV2.CreateSubset, {
            matterId,
            subsetId: result.id,
            documentCount: selectedDocIds.length,
          });
        }
      } else {
        result = await updateSubset.mutateAsync({
          matterId,
          subsetId: subsetId!,
          body: {
            name: subsetName,
            legalIssues: subsetIssues,
            docIds: selectedDocIds,
          },
        });

        // Track subset update
        if (result) {
          trackCustomEvent(TrackingEventsV2.UpdateSubset, {
            matterId,
            subsetId: result.id,
            documentCount: selectedDocIds.length,
            docsAdded: documentChanges.added,
            docsRemoved: documentChanges.removed,
          });
        }
      }

      // Mark suggestion as accepted if this was created from a suggestion
      if (result && mode === 'create' && suggestionId) {
        await updateSuggestionStatus.mutateAsync({
          suggestionId,
          status: 'accepted' as SubsetSuggestionStatus.Accepted, // enum can't be used directly on FE - don't use enums in future
        });
      }

      if (result && onSuccess) {
        onSuccess(result.id);
      } else if (result) {
        navigate(`/matters/${matterId}/subsets/${result.id}`);
      }

      // Close the dialog if it was open
      setShowConfirmDialog(false);
    } catch (error) {
      console.error(`Failed to ${mode} subset:`, error);
    }
  };

  const isLoading = mode === 'create' ? createSubset.isLoading : updateSubset.isLoading;
  const buttonText = mode === 'create' ? 'Create Subset' : 'Update Subset';
  const loadingText = mode === 'create' ? 'Creating...' : 'Updating...';

  return (
    <PageContainer className="flex h-full flex-col overflow-hidden px-0">
      <div className="flex grow overflow-hidden">
        <section className="flex flex-col">
          <div className="flex items-center gap-4 p-7 pr-5">
            <PageTitle text={mode === 'create' ? 'Create Subset' : 'Edit Subset'} iconClass="lucide--group" />

            <Popover>
              <PopoverTrigger>
                <Icon className="lucide--info mt-1.25 text-base text-gray-400 hover:text-gray-600" />
              </PopoverTrigger>

              <PopoverContent side="right" sideOffset={14} className="mt-4 text-gray-800">
                <div className="px-1">
                  Subsets allow you to easily {t('analyse')} facts from a select group of documents.
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div
            className="m-2 mt-0 flex w-sm grow flex-col justify-between overflow-y-hidden rounded-md border bg-gray-50 shadow-lg/2"
            data-tour="subset-form"
          >
            <div className="flex grow flex-col justify-between overflow-y-auto">
              <div className="flex flex-col gap-2 px-5 py-2">
                <div className="mt-2 flex items-center gap-1">
                  <label>
                    <div className="flex items-center gap-2 px-1">
                      <p className="text-gray-700">
                        Subset Name <span className="text-teal-500">*</span>
                      </p>
                    </div>
                  </label>

                  {nameHasChanged && (
                    <button
                      type="button"
                      onClick={() => {
                        setSubsetName(initialData?.name ?? '');
                        setShowNameError(false);
                      }}
                      className="animate-in fade-in w-fit text-gray-400 duration-100 hover:text-gray-600"
                      title="Undo changes"
                    >
                      <Icon className="lucide--rotate-ccw text-sm" />
                    </button>
                  )}
                </div>
                <Input
                  type="text"
                  placeholder="John Doe's Emails"
                  value={subsetName}
                  onChange={(e) => {
                    setSubsetName(e.target.value);
                    setShowNameError(false);
                  }}
                  className={showNameError ? 'border-red-500' : ''}
                />
                {showNameError && <p className="px-1 text-sm text-red-500">Subset name is required</p>}

                <div>
                  <div className="mt-2 flex items-center gap-1">
                    <label>
                      <div className="flex items-center gap-2 px-1">
                        <p className="text-gray-700">
                          Subset Issues <span className="text-teal-500">*</span>
                        </p>
                      </div>
                    </label>

                    {issuesHaveChanged && (
                      <button
                        type="button"
                        onClick={() => {
                          setSubsetIssues(initialData?.legalIssues ?? '');
                          setShowIssuesError(false);
                        }}
                        className="animate-in fade-in w-fit text-gray-400 duration-100 hover:text-gray-600"
                        title="Undo changes"
                      >
                        <Icon className="lucide--rotate-ccw text-sm" />
                      </button>
                    )}
                  </div>
                  <p className="my-2 mb-4 px-1 text-gray-500">
                    Subset-specific issues are used to determine the relevance classification of facts.
                  </p>
                  <Textarea
                    placeholder="John Doe's awareness of fraudulent activity"
                    value={subsetIssues}
                    onChange={(e) => {
                      setSubsetIssues(e.target.value);
                      setShowIssuesError(false);
                    }}
                    rows={14}
                    className={showIssuesError ? 'border-red-500' : ''}
                  />
                  {showIssuesError && <p className="mt-1 px-1 text-sm text-red-500">Subset issues are required</p>}
                  {matterIssues && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        setSubsetIssues(matterIssues);
                        setShowIssuesError(false);
                        trackCustomEvent(TrackingEventsV2.CopyMatterIssuesToSubset, { matterId });
                      }}
                    >
                      <Icon className="lucide--copy" />
                      Copy Matter Issues
                    </Button>
                  )}
                </div>
              </div>

              {mode === 'create' && (
                <p className="flex items-center gap-3 p-5 pr-6 text-balance text-gray-500">
                  <Icon className="lucide--info mb-0.75 text-gray-400" />
                  You can always add or remove documents from a Subset after creation.
                </p>
              )}
            </div>

            <div className="flex w-full items-center justify-between rounded-b-md border-t bg-white p-4">
              {mode === 'edit' && (documentChanges.added > 0 || documentChanges.removed > 0) ? (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Icon className="lucide--file h-3.5 w-3.5 text-gray-400" />
                  <span>
                    {documentChanges.added > 0 && (
                      <span className="text-green-600">+{documentChanges.added} added</span>
                    )}
                    {documentChanges.added > 0 && documentChanges.removed > 0 && <span>, </span>}
                    {documentChanges.removed > 0 && (
                      <span className="text-red-600">-{documentChanges.removed} removed</span>
                    )}
                  </span>
                </div>
              ) : (
                <div />
              )}

              <Button
                variant="primary"
                className="w-fit"
                disabled={!selectedCount || isLoading || !hasChanges}
                onClick={handleButtonClick}
              >
                <Icon className="lucide--group" />
                <span>
                  {isLoading
                    ? loadingText
                    : mode === 'create'
                      ? `${buttonText} with ${selectedCount} Document${selectedCount === 1 ? '' : 's'}`
                      : buttonText}
                </span>
              </Button>
            </div>
          </div>
        </section>

        <DocPickerTable
          docs={docs}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          allDocumentsCaseId={allDocumentsCase?.id ?? ''}
          matterId={matterId}
          mode={mode}
          initialDocIds={mode === 'edit' ? initialDocIds : undefined}
          defaultShowSelected={!!suggestionId}
          isLoadingInitialSelection={isLoadingInitialData || isLoadingDocs}
          processingDocIds={processingDocIds}
        />
      </div>

      <ConfirmationModal
        isOpen={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        title="Update Subset"
        onConfirm={handleSubmit}
        isLoading={isLoading}
      >
        <div className="space-y-5">
          <p className="text-gray-700">You are about to update this subset with the following changes:</p>

          <div className="ml-4 space-y-2">
            {nameHasChanged && (
              <div className="flex items-center gap-3 text-sm">
                <Icon className="lucide--pencil" />
                <span className="text-gray-700">
                  Name changed to: <span className="font-medium">{subsetName}</span>
                </span>
              </div>
            )}

            {issuesHaveChanged && (
              <div className="flex items-center gap-3 text-sm">
                <Icon className="lucide--pencil" />

                <span className="text-gray-700">Legal issues updated</span>
              </div>
            )}

            {documentChanges.added > 0 && (
              <div className="flex items-center gap-3 text-sm">
                <Icon className="lucide--file-plus" />
                <span>
                  <span className="font-semibold">{documentChanges.added}</span> document
                  {documentChanges.added === 1 ? '' : 's'} added
                </span>
              </div>
            )}

            {documentChanges.removed > 0 && (
              <div className="flex items-center gap-3 text-sm">
                <Icon className="lucide--file-minus" />
                <span>
                  <span className="font-semibold">{documentChanges.removed}</span> document
                  {documentChanges.removed === 1 ? '' : 's'} removed
                </span>
              </div>
            )}
          </div>
        </div>
      </ConfirmationModal>
    </PageContainer>
  );
}
